/**
 * Italian public holidays + Lodi patron saint.
 *
 * Hardcoded because the list is stable. Admin-flagged closures (the
 * `closures` table) are a separate, more authoritative layer — when
 * iCons closes the office for a non-holiday reason, admins flag it
 * via the Closures panel.
 */
import { dateKey } from "./dates";

interface Holiday {
  /** "YYYY-MM-DD" key for the year being computed. */
  key: string;
  /** Human-readable name (Italian, since the audience is iCons). */
  name: string;
}

/** Anonymous Gregorian algorithm — gives Easter Sunday's date for any
 *  year in the Gregorian calendar. We add 1 day for Easter Monday
 *  (Pasquetta) which is the public holiday in Italy. */
function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const L = (32 + 2 * e + 2 * i - h - k) % 7;
  const mth = Math.floor((a + 11 * h + 22 * L) / 451);
  const month = Math.floor((h + L - 7 * mth + 114) / 31);
  const day = ((h + L - 7 * mth + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

/** Build the holiday set for a single year. Cached per-year because
 *  the list never changes mid-session. */
const cache = new Map<number, Map<string, Holiday>>();

function buildYear(year: number): Map<string, Holiday> {
  const map = new Map<string, Holiday>();
  const add = (m: number, d: number, name: string): void => {
    const key = dateKey(year, m, d);
    map.set(key, { key, name });
  };
  // Fixed-date national holidays (month is 0-indexed)
  add(0, 1, "Capodanno");                       // 1 Jan
  add(0, 6, "Epifania");                        // 6 Jan
  add(3, 25, "Festa della Liberazione");        // 25 Apr
  add(4, 1, "Festa del Lavoro");                // 1 May
  add(5, 2, "Festa della Repubblica");          // 2 Jun
  add(7, 15, "Ferragosto");                     // 15 Aug
  add(10, 1, "Ognissanti");                     // 1 Nov
  add(11, 8, "Immacolata Concezione");          // 8 Dec
  add(11, 25, "Natale");                        // 25 Dec
  add(11, 26, "Santo Stefano");                 // 26 Dec

  // Lodi patron saint (city-specific, observed locally)
  add(0, 19, "San Bassiano (patrono di Lodi)"); // 19 Jan

  // Variable: Easter Monday = Easter Sunday + 1
  const easter = easterSunday(year);
  const monday = new Date(easter);
  monday.setDate(monday.getDate() + 1);
  const k = dateKey(monday.getFullYear(), monday.getMonth(), monday.getDate());
  map.set(k, { key: k, name: "Lunedì dell'Angelo (Pasquetta)" });

  return map;
}

/** Returns the holiday for a given ISO date key, or null. */
export function getHoliday(isoDate: string): Holiday | null {
  const year = Number(isoDate.slice(0, 4));
  if (!Number.isFinite(year)) return null;
  let yearMap = cache.get(year);
  if (!yearMap) {
    yearMap = buildYear(year);
    cache.set(year, yearMap);
  }
  return yearMap.get(isoDate) ?? null;
}
